import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tasksKeys } from "@/application/query-keys/tasks.keys";
import {
  tasksRepository,
  type TaskFilters,
} from "@/infrastructure/repositories/tasks.supabase.repository";
import type { TaskRow } from "@/infrastructure/supabase/database.types";

export function useTasksList(filters: TaskFilters = {}) {
  return useQuery({
    queryKey: tasksKeys.list(filters),
    queryFn: () => tasksRepository.list(filters),
  });
}

export function useTaskDetail(id: string | undefined) {
  return useQuery({
    queryKey: tasksKeys.detail(id ?? ""),
    queryFn: () => tasksRepository.byId(id as string),
    enabled: !!id,
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<TaskRow, "id" | "created_at" | "updated_at">) =>
      tasksRepository.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tasksKeys.all });
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<TaskRow> }) =>
      tasksRepository.update(id, patch),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: tasksKeys.all });
      queryClient.setQueryData(tasksKeys.detail(data.id), data);
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tasksRepository.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tasksKeys.all });
    },
  });
}
